<template>
  <div class="bg-white rounded-lg shadow p-6">
    <!-- Header with file info if viewing specific upload -->
    <div v-if="fileName" class="flex items-center justify-between mb-4">
      <div>
        <h3 class="font-semibold">{{ fileName }}</h3>
        <p class="text-sm text-gray-600">{{ new Date(uploadDate).toLocaleDateString('sv-SE') }}</p>
      </div>
      <button class="px-4 py-2 border border-gray-300 rounded-md text-sm" @click="$emit('clear-selection')">Visa senaste data</button>
    </div>

    <!-- Search -->
    <div class="mb-4">
      <div class="relative">
        <BaseIcon icon="search" class="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input type="text" v-model="searchTerm" placeholder="Sök i data..." class="w-full pl-10 pr-4 py-2 border rounded-md" />
      </div>
    </div>

    <!-- Table -->
    <div class="rounded-md border overflow-auto max-h-[500px]">
      <table class="w-full">
        <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th v-for="col in displayColumns" :key="col" class="text-left p-2 border-b cursor-pointer hover:bg-gray-100 whitespace-nowrap" @click="handleSort(col)">
              <div class="flex items-center gap-1">
                {{ col }}
                <BaseIcon icon="sort" class="text-gray-400 text-xs" />
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="filteredAndSortedData.length === 0">
            <td :colspan="displayColumns.length" class="text-center text-gray-500 p-4">Ingen data hittades</td>
          </tr>
          <tr v-for="(row, rowIndex) in filteredAndSortedData" :key="rowIndex" class="hover:bg-gray-50 border-b">
            <td v-for="col in displayColumns" :key="col" class="p-2 whitespace-nowrap">
              <div
                v-if="isTruncatedCol(col) && cellText(row, col).length > 30"
                class="relative inline-block"
                @mouseenter="hoveredCell = rowIndex + '-' + col"
                @mouseleave="hoveredCell = null"
              >
                <span class="cursor-default">{{ cellText(row, col).slice(0, 30) }}&hellip;</span>
                <div
                  v-if="hoveredCell === rowIndex + '-' + col"
                  :class="[
                    'absolute bottom-full mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg pointer-events-none',
                    col === 'Beskrivning' ? 'right-0 whitespace-normal break-words' : 'left-0 whitespace-nowrap'
                  ]"
                  :style="{ zIndex: 99999, maxWidth: col === 'Beskrivning' ? '320px' : 'none' }"
                >
                  {{ cellText(row, col) }}
                  <div
                    class="absolute top-full w-0 h-0"
                    :class="col === 'Beskrivning' ? 'right-4' : 'left-4'"
                    style="border-left: 5px solid transparent; border-right: 5px solid transparent; border-top: 5px solid #111827;"
                  ></div>
                </div>
              </div>
              <template v-else>{{ cellText(row, col) }}</template>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="mt-4 text-sm text-gray-600">
      Visar {{ formatNumber(filteredAndSortedData.length) }} av {{ formatNumber(data.length) }} rader
    </div>


  </div>
</template>

<script>
export default {
  name: "PipelineDataTable",
  props: {
    data: { type: Array, required: true },
    fileName: { type: String, default: "" },
    uploadDate: { type: String, default: "" },
  },
  data() {
    return {
      searchTerm: "",
      debouncedSearchTerm: "",
      searchDebounceTimer: null,
      sortColumn: null,
      sortDirection: "asc",
      hoveredCell: null,
    };
  },
  computed: {
    displayColumns() {
      if (this.data.length === 0) return [];
      // Only show clean named columns; filter out col_N keys and raw Excel header keys
      const knownColumns = ["Säljare", "Avslutsdatum", "Företag", "Ordervärde", "Fas",
        "Kontaktperson", "OrderID", "Beskrivning", "Antal"];
      const keys = Object.keys(this.data[0]);
      const named = knownColumns.filter(c => keys.includes(c));
      if (named.length > 0) return named;
      // Fallback: remove col_N and "Order:" headers for legacy data
      return keys.filter(k => !k.match(/^col_\d+$/) && !k.startsWith('Order:'));
    },
    filteredAndSortedData() {
      let result = [...this.data];
      if (this.debouncedSearchTerm) {
        const term = this.debouncedSearchTerm.toLowerCase();
        result = result.filter((row) => Object.values(row).some((value) => String(value).toLowerCase().includes(term)));
      }
      if (this.sortColumn) {
        result.sort((a, b) => {
          const aVal = a[this.sortColumn];
          const bVal = b[this.sortColumn];
          if (aVal === bVal) return 0;
          const comparison = aVal > bVal ? 1 : -1;
          return this.sortDirection === "asc" ? comparison : -comparison;
        });
      }
      return result;
    },
  },
  watch: {
    searchTerm(val) {
      clearTimeout(this.searchDebounceTimer);
      this.searchDebounceTimer = setTimeout(() => {
        this.debouncedSearchTerm = val;
      }, 300);
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer);
  },
  methods: {
    cellText(row, col) {
      const val = row[col];
      return val !== undefined && val !== null ? String(val) : '-';
    },
    isTruncatedCol(col) {
      return col === 'Företag' || col === 'Beskrivning';
    },
    formatNumber(num) {
      return num.toLocaleString("sv-SE", { maximumFractionDigits: 0 });
    },
    handleSort(column) {
      if (this.sortColumn === column) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = column;
        this.sortDirection = "asc";
      }
    },

  },
};
</script>
